<template>
  <v-layout>
    <v-flex>
      <v-layout align-center>
        <h1 class="headline">Head Offices</h1>
        <v-spacer></v-spacer>
        <v-col class="py-0">
          <list-filters
            :filters="filters"
            label="Find Head Office"
            searchButtonId="findHeadOfficeBtn"
            ref-name="headOfficesListFilters"
            @searchParamsChanged="searchParamsChanged"
            :expansionPanel="false"
          ></list-filters>
        </v-col>
        <div>
          <v-container>
            <list-visible-columns-and-filter
              :visible-columns="visibleColumns"
              :selectable-headers="selectableHeaders"
              v-model="search"
              @change="onVisibleColumnsChange"
            ></list-visible-columns-and-filter>
          </v-container>
        </div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" fab dark color="primary" @click="createItem">
              <v-icon dark>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Create New Head Office</span>
        </v-tooltip>
      </v-layout>

      <v-card>
        <v-data-table
          :headers="filteredHeaders"
          :items="headOffices.edges"
          :loading="$apollo.loading"
          v-bind="pagingOptions"
          :search="search"
          item-key="node.id"
        >
          <template v-slot:item.node.notInUse="{ item }">
            <v-simple-checkbox
              v-model="item.node.notInUse"
              disabled
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              :hide-view="true"
              @deleteItem="deleteItem($event)"
              @editItem="editItem($event)"
            ></default-list-actions>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog
        v-model="headOfficeEditorDialog"
        persistent
        width="700"
        scrollable
      >
        <v-card>
          <head-office-editor
            :headOffice="selectedHeadOffice"
            :doSaveHeadOffice="doSaveHeadOffice"
            :headOfficeEditorDialog="headOfficeEditorDialog"
            @saved="onSaved($event)"
            @onResetSave="onResetSave"
          ></head-office-editor>
          <v-card-actions class="grey lighten-4">
            <v-spacer></v-spacer>
            <v-btn color="warning" text @click="closeDialog">Cancel</v-btn>
            <v-btn color="primary" @click="onSaveHeadOffice">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
  </v-layout>
</template>

<script>
import {
  ALL_HEAD_OFFICES_SEARCH_QUERY,
  HEAD_OFFICE_DELETE_MUTATION,
} from "../../../../constants/graphql";
import HeadOfficeEditor from "./HeadOfficeEditor";
import headOfficesFilters from "./head-offices-filters";
import DefaultListActions from "../../shared/DefaultListActions";
import ListFilters from "../../shared/list-filters/ListFilters";
import SimpleListMixin from "../../../../mixins/SimpleListMixin";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";

export default {
  name: "HeadOfficesList",
  mixins: [SimpleListMixin],
  components: {
    ListFilters,
    DefaultListActions,
    HeadOfficeEditor,
    ListVisibleColumnsAndFilter,
  },

  data() {
    return {
      search: "",
      searchParams: { not_in_use_eq: false },
      selectedHeadOffice: {},
      headOffices: {},
      doSaveHeadOffice: false,
      headOfficeEditorDialog: false,
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "name",
        "code",
        "email",
        "telephone",
        "actions",
      ]),
      headOfficesFilters: headOfficesFilters,
      headers: [
        {
          text: "Name",
          value: "node.name",
          name: "name",
        },
        {
          text: "Code",
          value: "node.code",
          name: "code",
        },
        {
          text: "Email",
          value: "node.email",
          name: "email",
        },
        {
          text: "Telephone",
          value: "node.telephone",
          name: "telephone",
        },
        {
          text: "Address",
          value: "node.address",
          name: "address",
        },
        {
          text: "Notes",
          value: "node.notes",
          name: "notes",
        },
        {
          text: "Not In Use",
          value: "node.notInUse",
          name: "notInUse",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
    };
  },

  computed: {
    filters() {
      return headOfficesFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
    filteredHeaders() {
      return this.headers.filter(
        (h) => this.visibleColumns.indexOf(h.name) > -1
      );
    },
  },

  methods: {
    searchParamsChanged(event) {
      this.searchParams = { ...event };
    },
    createItem() {
      this.selectedHeadOffice = {};
      this.headOfficeEditorDialog = true;
    },
    editItem(item) {
      this.selectedHeadOffice = item.node;
      this.headOfficeEditorDialog = true;
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: HEAD_OFFICE_DELETE_MUTATION,
          variables: {
            headOfficeId: item.node.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", { msg: "Head office removed." });

      if (this.$apollo.queries.headOffices != null) {
        this.$apollo.queries.headOffices.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      this.$store.dispatch("showAppSnackbar", {
        msg: err.graphQLErrors[0].message,
        color: "error",
      });
    },
    onSaved(context) {
      if (this.$apollo.queries.headOffices != null) {
        this.$apollo.queries.headOffices.refetch();
      }
      this.onResetSave();
      this.headOfficeEditorDialog = false;
      this.selectedHeadOffice = {};
      let msg =
        context == "edit" ? "Head office updated." : "Head office created.";
      this.$store.dispatch("showAppSnackbar", { msg });
    },
    onResetSave() {
      this.doSaveHeadOffice = false;
    },
    closeDialog() {
      this.headOfficeEditorDialog = false;
      this.selectedHeadOffice = {};
    },
    onSaveHeadOffice() {
      this.doSaveHeadOffice = true;
    },
    showColumn(fieldName) {
      return this.visibleColumns.find((name) => name === fieldName);
    },
  },

  apollo: {
    headOffices: {
      query: ALL_HEAD_OFFICES_SEARCH_QUERY,
      variables() {
        return {
          filter: {
            ...this.searchParams,
          },
        };
      },
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {},
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/admin/department-types/HeadOfficesList.vue